import {
  Box,
  Button,
  Container,
  FormField,
  Input,
  SpaceBetween,
  Spinner,
  Header,
  ColumnLayout,
  ExpandableSection,
  StatusIndicator,
  Icon,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useQueryExplorerQuery } from './hooks/useQueryExplorerQuery';
import { useEmrJobDashboardQuery } from './hooks/useEmrJobDashboardQuery';
import { CodeViewer } from '../Components/Common/CodeViewer';

const REDACTED_KEYS = ['EncryptedFAS'];

const QUERY_INFO_KEYS = [
  'QueryId', 'AccountId', 'QueryStatus', 'LanguageType', 'JobType',
  'SubmittedTime', 'LastUpdatedTime', 'SubmittedBy',
];

const redactSensitiveFields = (data: any): any => {
  if (!data || typeof data !== 'object') return data;
  const redacted = { ...data };
  for (const key of REDACTED_KEYS) {
    if (key in redacted) {
      redacted[key] = '[REDACTED]';
    }
  }
  return redacted;
};

const COPYABLE_KEYS = ['FlintQueryIdentifier'];

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <span
      style={{ cursor: 'pointer', marginLeft: 4 }}
      title="Copy to clipboard"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Icon name={copied ? 'status-positive' : 'copy'} size="small" />
    </span>
  );
};

const ValueDisplay = ({ value, copyable }: { value: any; copyable?: boolean }) => {
  if (value === null || value === undefined || value === '') return <div>N/A</div>;
  const suffix = copyable ? <CopyButton text={String(value)} /> : null;
  if (typeof value === 'object') return <span><Box variant="code" fontSize="body-s">{JSON.stringify(value)}</Box>{suffix}</span>;
  const str = String(value);
  if (str.includes('arn:') || str.includes('https://') || str.length > 60) {
    return <span><Box variant="code" fontSize="body-s">{str}</Box>{suffix}</span>;
  }
  return <span>{str}{suffix}</span>;
};

const QueryExplorerPage = () => {
  const [inputValue, setInputValue] = useState('');
  const [getQueryDetails, { loading, data: queryData }] = useQueryExplorerQuery();
  const [getEmrDashboard, { loading: emrLoading, data: emrData }] = useEmrJobDashboardQuery();

  const handleSubmit = () => {
    getQueryDetails({ variables: { queryId: inputValue.trim() } });
  };

  const rawResult = get(queryData, 'describeQueryId', null);
  const queryResult = rawResult ? redactSensitiveFields(rawResult) : null;
  const command = `aes dqs describe-query-id --query-id ${inputValue.trim()}`;
  const hasResult = queryResult && Object.keys(queryResult).length > 0;

  // Auto-fetch EMR logs when FlintApplicationId, FlintJobId, FlintCellId are all present
  useEffect(() => {
    if (
      queryResult?.FlintApplicationId &&
      queryResult?.FlintJobId &&
      queryResult?.FlintCellId
    ) {
      getEmrDashboard({
        variables: {
          appId: queryResult.FlintApplicationId,
          flintCell: queryResult.FlintCellId,
          jobId: queryResult.FlintJobId,
        },
      });
    }
  }, [rawResult]);

  const emrLogUrl = get(emrData, 'fetchEmrJobDashboard', null);

  const queryInfoEntries = hasResult
    ? QUERY_INFO_KEYS.filter(k => k in queryResult).map(k => [k, queryResult[k]])
    : [];
  const otherEntries = hasResult
    ? Object.entries(queryResult).filter(([k]) => !QUERY_INFO_KEYS.includes(k) && k !== '__typename')
    : [];

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Look up query details by Query ID (UUID)">
        <span style={{ color: '#0972d3' }}>Query Explorer</span>
      </Header>

      <Container>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={<strong>Search Query</strong>}
            description="Enter a Query ID (UUID format)"
            constraintText="Format: UUID (e.g., 550e8400-e29b-41d4-a716-446655440000)"
          >
            <SpaceBetween direction="horizontal" size="xs">
              <div style={{ width: '600px' }}>
                <Input
                  onChange={({ detail }) => setInputValue(detail.value)}
                  value={inputValue}
                  inputMode="text"
                  placeholder="550e8400-e29b-41d4-a716-446655440000"
                  disabled={loading}
                />
              </div>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!inputValue.trim() || loading}
                loading={loading}
              >
                Search
              </Button>
            </SpaceBetween>
          </FormField>
          {inputValue && (
            <Box variant="awsui-key-label">
              CLI Command: <Box variant="code">{command}</Box>
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {loading && (
        <Container>
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>Loading query details...</Box>
          </Box>
        </Container>
      )}

      {!loading && hasResult && (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Query Information</span></Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              {queryInfoEntries.map(([key, value]) => (
                <div key={key as string}>
                  <Box variant="awsui-key-label">{key as string}</Box>
                  {key === 'QueryStatus' ? (
                    <StatusIndicator type={value === 'success' ? 'success' : value === 'failed' ? 'error' : 'info'}>
                      {String(value)}
                    </StatusIndicator>
                  ) : (
                    <ValueDisplay value={value} />
                  )}
                </div>
              ))}
              {emrLoading && (
                <div>
                  <Box variant="awsui-key-label">EMR Logs</Box>
                  <Spinner size="normal" />
                </div>
              )}
              {!emrLoading && emrLogUrl && typeof emrLogUrl === 'string' && (
                <div>
                  <Box variant="awsui-key-label">EMR Logs</Box>
                  <a href={emrLogUrl} target="_blank" rel="noopener noreferrer">
                    Open EMR Dashboard
                  </a>
                </div>
              )}
            </ColumnLayout>
          </Container>

          {otherEntries.length > 0 && (
            <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Other Details</span></Header>}>
              <ColumnLayout columns={2} variant="text-grid">
                {otherEntries.map(([key, value]) => (
                  <div key={key}>
                    <Box variant="awsui-key-label">{key}</Box>
                    <ValueDisplay value={value} copyable={COPYABLE_KEYS.includes(key)} />
                  </div>
                ))}
              </ColumnLayout>
            </Container>
          )}

          <ExpandableSection headerText="Full JSON Response" variant="container">
            <CodeViewer
              codeData={JSON.stringify(queryResult, null, 2)}
              language={{ label: 'json', value: 'json' }}
              theme={{ label: 'coy', value: 'coy' }}
            />
          </ExpandableSection>
        </SpaceBetween>
      )}

      {!loading && queryResult && Object.keys(queryResult).length === 0 && (
        <Container>
          <Box textAlign="center" color="inherit">
            <b>No results found for this Query ID</b>
          </Box>
        </Container>
      )}
    </SpaceBetween>
  );
};

export { QueryExplorerPage };
